import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/Spinner";
import { Camera, ScanLine, Keyboard } from "lucide-react";
import type { PortalState, VehicleData } from "@/store/portal-store";

interface Step4Props {
  state: PortalState;
  updateState: (partial: Partial<PortalState>) => void;
  onNext: () => void;
}

const MOCK_VEHICLE: Omit<VehicleData, "vin" | "acv"> = {
  year: "2022",
  make: "Freightliner",
  model: "Cascadia",
  bodyType: "Truck Tractor",
  gvwr: "52,000 lbs",
};

export function Step4VinAcv({ state, updateState, onNext }: Step4Props) {
  const [mode, setMode] = useState<"camera" | "manual">("camera");
  const [vin, setVin] = useState("");
  const [acv, setAcv] = useState("");
  const [vehicle, setVehicle] = useState<VehicleData | null>(null);
  const [scanning, setScanning] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [error, setError] = useState("");
  const [cameraBlocked, setCameraBlocked] = useState(false);

  const vinValid = /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin);
  const acvValid = acv.length > 0 && parseFloat(acv.replace(/[^0-9.]/g, "")) > 0;

  const handleScan = () => {
    setScanning(true);
    setTimeout(() => {
      setScanning(false);
      const mockVin = "1FUJHHDR3CLBP8899";
      setVin(mockVin);
      decodeVin(mockVin);
    }, 2500);
  };

  const decodeVin = (vinStr: string) => {
    setDecoding(true);
    setError("");
    setTimeout(() => {
      setDecoding(false);
      setVehicle({
        vin: vinStr,
        acv: "",
        ...MOCK_VEHICLE,
      });
    }, 1500);
  };

  const handleManualDecode = () => {
    if (vinValid) {
      decodeVin(vin);
    }
  };

  const formatCurrency = (value: string) => {
    const num = value.replace(/[^0-9]/g, "");
    if (!num) return "";
    return new Intl.NumberFormat("en-US").format(parseInt(num));
  };

  const handleConfirm = () => {
    if (vehicle) {
      updateState({
        vehicle: { ...vehicle, acv: acv.replace(/[^0-9]/g, "") },
      });
      onNext();
    }
  };

  return (
    <div className="flex flex-col px-6 py-6">
      <h2 className="text-xl font-bold mb-2">Scan Your Truck's VIN</h2>
      <p className="text-gray-500 text-sm mb-6">
        Point your camera at the VIN plate on your truck.
      </p>

      {!vehicle && !cameraBlocked && mode === "camera" && (
        <>
          {/* Camera viewfinder */}
          <div
            className="bg-gray-900 rounded-2xl h-48 flex flex-col items-center justify-center mb-4 relative overflow-hidden cursor-pointer"
            onClick={handleScan}
          >
            {scanning ? (
              <>
                <div className="w-56 h-16 border-2 border-green-400 rounded-lg" />
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-green-400 animate-[scan_1.5s_ease-in-out_infinite]" />
                <p className="text-green-400 text-sm mt-3 animate-pulse">
                  Scanning...
                </p>
              </>
            ) : (
              <>
                <ScanLine className="w-12 h-12 text-white/60 mb-2" />
                <p className="text-white/60 text-sm">Tap to scan VIN</p>
                <div className="w-56 h-12 border border-white/30 rounded-lg absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => setMode("manual")}
            className="flex items-center justify-center gap-2 text-sm text-blue-500 py-2 mb-4"
          >
            <Keyboard className="w-4 h-4" />
            Enter VIN Manually
          </button>
        </>
      )}

      {(mode === "manual" || cameraBlocked) && !vehicle && (
        <div className="space-y-3 mb-4">
          {cameraBlocked && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-2">
              <p className="text-sm text-yellow-700">
                Camera access is required to scan your VIN.
              </p>
            </div>
          )}
          <Label htmlFor="vin">VIN (17 characters)</Label>
          <div className="flex gap-2">
            <Input
              id="vin"
              type="text"
              placeholder="Enter 17-character VIN"
              value={vin}
              onChange={(e) => {
                setVin(
                  e.target.value
                    .toUpperCase()
                    .replace(/[^A-HJ-NPR-Z0-9]/gi, "")
                    .slice(0, 17)
                );
                setError("");
              }}
              className="h-12 text-base font-mono"
            />
            <Button
              onClick={handleManualDecode}
              disabled={!vinValid || decoding}
              className="h-12 px-6"
            >
              {decoding ? <Spinner size="sm" /> : "Decode"}
            </Button>
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
          {!cameraBlocked && (
            <button
              type="button"
              onClick={() => setMode("camera")}
              className="flex items-center justify-center gap-2 text-sm text-blue-500 py-2"
            >
              <Camera className="w-4 h-4" />
              Use Camera Instead
            </button>
          )}
        </div>
      )}

      {decoding && (
        <div className="flex items-center justify-center py-8 gap-2">
          <Spinner size="sm" />
          <span className="text-sm text-gray-500">Decoding VIN...</span>
        </div>
      )}

      {vehicle && (
        <>
          <Card className="mb-4">
            <CardContent className="pt-4 space-y-2">
              <p className="text-xs text-gray-400 font-mono">{vehicle.vin}</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400">Year</span>
                  <p className="font-medium">{vehicle.year}</p>
                </div>
                <div>
                  <span className="text-gray-400">Make</span>
                  <p className="font-medium">{vehicle.make}</p>
                </div>
                <div>
                  <span className="text-gray-400">Model</span>
                  <p className="font-medium">{vehicle.model}</p>
                </div>
                <div>
                  <span className="text-gray-400">Body Type</span>
                  <p className="font-medium">{vehicle.bodyType}</p>
                </div>
                <div>
                  <span className="text-gray-400">GVWR</span>
                  <p className="font-medium">{vehicle.gvwr}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2 mb-6">
            <Label htmlFor="acv">Actual Cash Value (ACV)</Label>
            <Input
              id="acv"
              type="text"
              inputMode="numeric"
              placeholder="$"
              value={acv ? `$${formatCurrency(acv)}` : ""}
              onChange={(e) => setAcv(e.target.value.replace(/[^0-9]/g, ""))}
              className="h-12 text-base"
            />
            <p className="text-xs text-gray-400">
              Enter the current market value of your truck.
            </p>
          </div>

          <Button
            className="w-full h-12 text-base"
            disabled={!acvValid}
            onClick={handleConfirm}
          >
            Confirm Vehicle
          </Button>
        </>
      )}
    </div>
  );
}
